import React, { useMemo, useState } from 'react';
import './AlertManagement.scss';
import {
  InternalApiAlertAssignment,
  InternalApiAlertStatusEvent,
  InternalApiAlertType,
  InternalApiScreeningAlertStatus,
} from '../../api-internal';
import { ApiNote } from '../../apis';
import { AlertManagementData } from './useAlertManagement';
import {
  HistoryEvent,
  HistoryEventType,
  HistoryModal,
} from '../../common/alert-section/history/HistoryModalWithToggle';
import { MenuItem, SalvMenuButton, Size } from '@salvapp/ui-components';

interface Props {
  alertId: number;
  alertType: InternalApiAlertType;
  alertState: AlertManagementData;
  alertStatuses: InternalApiScreeningAlertStatus[];
}

export const AlertHistory = ({ alertId, alertType, alertState, alertStatuses }: Props) => {
  const historyEvents = useMemo(() => {
    return toHistory(alertState, alertStatuses);
  }, [alertState, alertStatuses]);
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <SalvMenuButton
        ariaLabel={'More options'}
        onAction={() => {
          setShowModal(!showModal);
        }}
        placement='bottom right'
        size={Size.MEDIUM}
      >
        <MenuItem title={'Show history'} value={'history'} />
      </SalvMenuButton>
      <HistoryModal
        setShowModal={setShowModal}
        showModal={showModal}
        events={historyEvents}
        alertId={alertId}
        alertType={alertType}
      />
    </>
  );
};

const toHistory = (
  alertState: AlertManagementData,
  alertStatuses: InternalApiScreeningAlertStatus[]
): HistoryEvent[] => {
  const { assignments, statusEvents, notes } = alertState;
  const events: HistoryEvent[] = [];
  assignments && events.push(...assignments.map((assignment) => toAssignmentEvent(assignment)));
  const statusChangesById = new Map(statusEvents.map((s) => [s.id!, s]));
  notes &&
    events.push(
      ...notes.map((note) => {
        const relatedStatusChange = statusChangesById.get(note.statusChangeId!);
        if (!relatedStatusChange) {
          return toNoteEvent(note);
        } else {
          return toStatusEvent(relatedStatusChange, alertStatuses, note);
        }
      })
    );
  const statusChangesWithNotes = new Set(notes.map((note) => note.statusChangeId));
  statusEvents &&
    events.push(
      ...statusEvents
        .filter((statusEvent) => !statusChangesWithNotes.has(statusEvent.id))
        .map((statusEvent) => toStatusEvent(statusEvent, alertStatuses))
    );
  return events;
};

const toAssignmentEvent = (assignment: InternalApiAlertAssignment): HistoryEvent => ({
  type: HistoryEventType.ASSIGNMENT,
  startTime: assignment.startTime || new Date(),
  assigner: assignment.assigner,
  assignee: assignment.assignee,
});
const toNoteEvent = (note: ApiNote): HistoryEvent => {
  return {
    type: HistoryEventType.NOTE,
    startTime: note.createdTime || new Date(),
    assigner: note.user,
    comment: note.content,
  };
};
const toStatusEvent = (
  statusEvent: InternalApiAlertStatusEvent,
  alertStatuses: InternalApiScreeningAlertStatus[],
  note?: ApiNote
): HistoryEvent => ({
  type: HistoryEventType.STATUS,
  startTime: statusEvent.startTime || new Date(),
  assigner: statusEvent.assigner,
  status: alertStatuses.find((it) => it.id == statusEvent.statusId)?.textName ?? 'Unknown',
  comment: note?.content,
});
